// The fight, joined (phase B: the combat, enemies and bodies streams wired into one game): @rpg/combat for the hero and
// every enemy, the enemies' brains on @rpg/nav's crowd with @rpg/ai's perception, attack tokens and encounter director,
// and the bodies module's poses, hit reactions and ragdolls, as kernel modules any scene with a hero on @rpg/controller
// adds. The arena (`?scene=arena`, ./arena.ts) and the valley (the default scene, src/valley/fight.ts) run it.
//
// One sim step, by system order in the one kernel game:
//   fight:act       (-10)  combat.begin (timers, phases); the hero's respawn; the hero's intent (the player's buffered
//                          presses, the held block, the weapon switch; or a bot's), its target asked of the targeting
//                          first (the lock, else the soft pick); a boss's phase (a weapon swap); the director; each
//                          enemy's senses (30 Hz, staggered) and tree, whose `strike` asks combat through the port;
//                          the tokens' grants
//   (the scene's controllers, 0: the hero steps with `heroDrive()`: combat's lunge or roll, rooted, or a knockback)
//   fight:move      (50)   the crowd: the hero placed for the others to avoid; each enemy's combat drive (rooted while
//                          it swings) or knockback over its brain's goal; the crowd steps; the hurt sensors moved to
//                          where everyone now stands
//   (the scene's physics step, 100; the bodies' ragdolls, 110)
//   fight:resolve   (150)  combat.resolve: hit volumes sweep the hurt sensors, contacts resolve, events go out (the
//                          fight's listeners alert enemies, release tokens and feed the director; the bodies module
//                          poses, reacts and ragdolls; the feedback director, view side, fans each hit out)
// State: combat's (`fight/Combat`) and the fight's (`fight/State`: the enemies, tokens, the director, noises, the
// hero's respawn, knockbacks) are kernel components, saved and hashed with the bodies' (`bodies/State`); the crowd
// rides the fight's save slice and comes back bit for bit. Ids: the hero is 0, enemies count up from 1 (fighter ids,
// not kernel entities). Hit-stop has one owner, the impact director (src/feedback/): combat gets no `clock.hitStop`
// and keeps its slow-mo.
import type { GameModule, World } from "@rpg/kernel";
import type { Actions, RngStreams } from "@rpg/runtime";
import type { CollisionWorld } from "@rpg/collision";
import { stickToWorld, type Controller, type ControllerState, type MoveDrive } from "@rpg/controller";
import { AttackTokens, BRAIN_FAULTS, EncounterDirector, compileTree, createPerception, createTreeState, perceive, tickTree, type CompiledTree, type DirectorState, type EncounterDef, type Noise, type TokenPoolState, type TreeJson } from "@rpg/ai";
import { NavCrowd, type CrowdSnapshot, type NavWorld } from "@rpg/nav";
import { chooseTarget, createCombat, emptyCombatState, type TargetWeights, type Combat, type CombatState, type FighterView, type Intent, type IntentKind, type WeaponDef } from "@rpg/combat";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";
import { combatContent, type CombatContent } from "../combat/content";
import { BODIES_C, bodiesTables, createBodies, type Bodies } from "../bodies/module";
import { BRAINS, type Archetype, type Brain, type EnemyState, type Vec3 } from "./brains";
import { targetingValues } from "../targets/targeting";

export const FIGHT_C = "fight/State";
export const FIGHT_COMBAT_C = "fight/Combat";
/** The component types the fight adds to the state hash. */
export const FIGHT_HASHED = [FIGHT_C, FIGHT_COMBAT_C, BODIES_C] as const;
/** The layers the fight adds to its scene's table: a hurt layer per team (sensors only) and the ragdolls' (they meet
 *  the ground and the solids, never each other or the characters). */
export const FIGHT_LAYERS = { hurtHero: [], hurtFoe: [], ragdoll: ["terrain", "solid"] };
/** The physics solver's iterations where ragdolls fall (the bodies stream's measure: 24 keeps the joints in their
 *  limits; 16 drifts to 0.2 rad). */
export const FIGHT_SOLVER_ITERATIONS = 24;
/** The hero's fighter id. */
export const HERO_ID = 0;
/** The hero's hurt sensor: a body of its own that follows the hero, made on the fight's first step (so a save from
 *  before the fight joined its scene still loads: src/save/schema.ts). */
export const HERO_HURT_BODY = "hero-hurt";
const enemyBody = (id: number): string => `enemy-${id}`;

/** Planted defects the fight owns (each switched beside what it breaks). */
export const FIGHT_FAULTS = {
  /** The attack-token pool ignores its cap (`@rpg/ai` `AttackTokens.faults.capRemoved`): the attackers-per-step
   *  invariant and the bot fight's log must go red. */
  tokenCapRemoved: false,
  /** Skip the crowd's per-step re-seat (`@rpg/nav` `NavCrowd.faults.noReseat`): a restored fight must drift. */
  crowdNoReseat: false,
  /** The brains ignore the attack tokens (`@rpg/ai` `BRAIN_FAULTS.ignoreTokens`: they ask and strike as if they held one): the
   *  attackers-per-step invariant and the bot fight's log must go red. */
  ignoreTokens: false,
};
export type FightFaultName = keyof typeof FIGHT_FAULTS;

/** An encounter as the fight runs it: the director's waves at named spawn points. */
export type FightEncounter = EncounterDef & {
  spawns: { id: string; x: number; z: number; spread: number; facing?: number }[];
  tokens: { maxHold: number; regrant: number };
  /** A wave arrives alert and the director pings the hero's place while it lasts (the arena's waves); off, a wave's
   *  enemies stand idle at their spawn until they sense the hero (the valley's camps). Default true. */
  hunt?: boolean;
};

/** The content the fight reads, references resolved. */
export interface FightContent {
  spike: SpikeContent;
  combat: CombatContent;
  kinds: Record<string, Archetype>;
  trees: Record<string, CompiledTree<Brain>>;
  difficulty: Record<string, { damageTaken: number; damageDealt: number; tokenCap: number }>;
  encounters: Record<string, FightEncounter>;
  tuning: { thinkEvery: number; respawn: number; corpse: number; huntPing: number };
}

/** The intents a weapon answers: its combo's first move for `attack`, then its skill and big move. */
export function movesetOf(w: WeaponDef | undefined): Partial<Record<IntentKind, string>> {
  if (!w) return {};
  return { ...(w.combo[0] ? { attack: w.combo[0] } : {}), ...(w.skill ? { skill: w.skill } : {}), ...(w.big ? { big: w.big } : {}) };
}

/** Read the fight's content. Throws naming what is missing. */
export function readFightContent(spike: SpikeContent): FightContent {
  const reg = spike.registry;
  const combat = combatContent(reg);
  const weapons = new Map(combat.weapons.map((w) => [w.id, w]));
  const weaponOf = (id: string, who: string): WeaponDef => {
    const w = weapons.get(id);
    if (!w) throw new Error(`fight: ${who} names a missing weapon "${id}"`);
    return w;
  };
  const kinds: Record<string, Archetype> = {};
  for (const row of reg.rows("enemies")) {
    const w = weaponOf(row.weapon.id, `kind ${row.id}`);
    kinds[row.id] = {
      id: row.id,
      card: row.card.id,
      tree: row.tree.id,
      hp: row.hp,
      poise: row.poise,
      body: { ...row.body },
      senses: structuredClone(row.senses) as unknown as Archetype["senses"],
      speed: { ...row.speed },
      weapon: w.id,
      moveset: movesetOf(w),
      stats: { attack: row.stats.attack, defence: row.stats.defence },
      tokenPriority: row.tokenPriority,
      noise: row.noise,
      phases: (row.phases ?? []).map((p) => {
        const pw = weaponOf(p.weapon.id, `kind ${row.id}'s phase`);
        return { atHp: p.atHp, card: p.card.id, weapon: pw.id, moveset: movesetOf(pw), speedScale: p.speedScale, summon: p.summon ? { kind: p.summon.kind.id, count: p.summon.count } : null };
      }),
    };
  }
  const trees: Record<string, CompiledTree<Brain>> = {};
  for (const r of reg.rows("behaviour-trees")) trees[r.id] = compileTree(r as unknown as TreeJson, BRAINS);
  const difficulty: FightContent["difficulty"] = {};
  for (const d of reg.rows("difficulty")) difficulty[d.id] = { damageTaken: d.damageTaken, damageDealt: d.damageDealt, tokenCap: d.tokenCap };
  const encounters: Record<string, FightEncounter> = {};
  for (const e of reg.rows("encounters")) {
    encounters[e.id] = {
      id: e.id,
      spawns: e.spawns.map((s) => ({ ...s })),
      quiet: e.quiet,
      loop: e.loop,
      intensity: { ...e.intensity },
      tokens: { ...e.tokens },
      ...(e.hunt !== undefined ? { hunt: e.hunt } : {}),
      waves: e.waves.map((w) => ({ id: w.id, ...(w.quiet !== undefined ? { quiet: w.quiet } : {}), ...(w.until !== undefined ? { until: w.until } : {}), groups: w.groups.map((g) => ({ kind: g.kind.id, count: g.count, at: g.at })) })),
    };
  }
  const missing: string[] = [];
  const need = (id: string): number => {
    const v = reg.get("tuning", id)?.value;
    if (typeof v !== "number") missing.push(id);
    return typeof v === "number" ? v : Number.NaN;
  };
  const tuning = { thinkEvery: need("arena.thinkEvery"), respawn: need("arena.respawn"), corpse: need("arena.corpse"), huntPing: need("arena.huntPing") };
  if (missing.length) throw new Error(`fight: missing tuning rows ${missing.join(", ")}`);
  for (const k of Object.values(kinds)) if (!trees[k.tree]) throw new Error(`fight: kind ${k.id} names a missing tree ${k.tree}`);
  return { spike, combat, kinds, trees, difficulty, encounters, tuning };
}

/** The fight's saved state, beside combat's and the bodies'. */
export interface FightState {
  nextId: number;
  heroId: number;
  difficulty: string;
  hero: { deadFor: number; respawns: number };
  enemies: EnemyState[];
  tokens: TokenPoolState;
  director: DirectorState | null;
  directorOn: boolean;
  /** Recent noises, with the step they were made on. */
  noises: (Noise & { step: number })[];
  steps: number;
  /** The hero's hurt sensor exists and the scene's start (its camps) is placed: done on the fight's first step. */
  placed: boolean;
  /** Knockbacks in progress (the bodies module's reactions): a ground velocity for so many steps. */
  knocks: { entity: number; vel: [number, number]; left: number }[];
  /** The held block came from the button (released with it). */
  heldBlock: boolean;
}

/** One logged event (for the view, the bot and the specs; capped). */
export type FightEvent = { step: number } & (
  | { type: "hit"; p: GameEvents["combat:hit"] }
  | { type: "defeated"; p: GameEvents["combat:defeated"] }
  | { type: "telegraph"; p: GameEvents["combat:telegraph"] }
  | { type: "move"; p: GameEvents["combat:move"] }
  | { type: "phase"; p: { entity: number; phase: number } }
  | { type: "alert"; p: { entity: number; level: string; cause: string | null } }
);

/** The attackers-per-step log of a fight (gate B4: never more attackers than the cap). */
export interface AttackLog {
  perStep: number[];
  caps: number[];
  logging: boolean;
}

/** A fight's totals since the last reset (diagnostics: not saved, not hashed). */
export interface FightTally {
  heroDamage: number;
  enemyDamage: number;
  dodged: number;
  heroDefeats: number;
  hits: Record<string, number>;
  moves: Record<string, number>;
  defeats: Record<string, number>;
}
const newTally = (): FightTally => ({ heroDamage: 0, enemyDamage: 0, dodged: 0, heroDefeats: 0, hits: {}, moves: {}, defeats: {} });

/** What the scene gives the fight. */
export interface FightHost {
  /** A streamed world's chunk at a point (the default scene): a ragdoll goes with its chunk when that unloads. */
  chunkOf?: (x: number, z: number) => string | null;
  collision: CollisionWorld;
  actions: Actions;
  rng: RngStreams;
  nav: NavWorld;
  content: FightContent;
  /** The runtime clock's slow-mo (combat's: the perfect dodge, the parry). */
  slowMo(scale: number, seconds: number, easeBack: number): void;
  /** The hero's controller state and controller. */
  hero(): ControllerState;
  heroCtl(): Controller;
  /** The camera's yaw the stick is relative to (the dodge's direction). */
  cameraYaw(): number;
  /** Where the hero stands again after a defeat. */
  respawnAt(): { x: number; z: number; yaw: number };
  terrainAt(x: number, z: number): number;
  /** Where a fighter that is neither the hero nor an enemy stands (phase C: breakables registered with combat as
   *  targets with a hurt sensor), or null. Absent: there are none. */
  otherPose?(id: number): { feet: Vec3; yaw: number } | null;
  /** Phase D1 (src/rpg/): the move a fighter starts for one it asks for (a skill tree's upgrade), and the weapons the
   *  hero owns (the quick switch cycles these). Absent: no swaps; every weapon with an attachment. */
  moveFor?(entity: number, move: string): string;
  ownedWeapons?(): string[];
  /** The encounter the director runs (null: none). */
  encounter: string | null;
  /** The director runs from the start (the arena's `?director=off` turns it off). */
  director: boolean;
  difficulty: string;
}

/** The fight, for the scene, its view, the handle and the bots. */
export interface Fight {
  modules: GameModule<GameEvents>[];
  /** Called at the end of the scene's content build. */
  build(world: World): void;
  hashed: readonly string[];
  combat: Combat;
  bodies: Bodies;
  collision: CollisionWorld;
  crowd: NavCrowd;
  tokens: AttackTokens;
  readonly director: EncounterDirector | null;
  content: FightContent;
  dt: number;
  state(): FightState;
  combatState(): CombatState;
  /** A fighter's combat view, or null (not a fighter). */
  view(id: number): FighterView | null;
  /** The hero's move override for its controller this step (a lunge, a roll, rooted, a knockback), or null. */
  heroDrive(): MoveDrive | null;
  /** The hero's intent for the next step instead of the player's input (a bot's or a spec's), taken once. */
  heroIntent: Intent | null;
  /** A bot drives the hero: the player's combat presses are ignored. */
  botDriving: boolean;
  /** What a hero's move turns to when it starts: the lock, else the soft pick (src/targets/; null: combat's own). */
  target: (() => number | null) | null;
  events: FightEvent[];
  eventsTotal(): number;
  log: AttackLog;
  tally: FightTally;
  /** Enemies attacking now: in a move's wind-up or active phase. */
  attackers(): number[];
  /** Spawn an enemy of `kind` at (x, z) (snapped to the navmesh), optionally alert. Returns its id. */
  spawn(kind: string, x: number, z: number, o?: { alert?: boolean; facing?: number }): number;
  /** Clear every enemy, reset the director, the tokens and the hero (a fresh fight at `difficulty`). */
  reset(o?: { difficulty?: string; director?: boolean }): void;
  setDirector(on: boolean): void;
  directorOn(): boolean;
  /** Whether any living enemy is alert to the hero (the combat input context, the lock button). */
  engaged(): boolean;
  /** Defeat an enemy at the next step as a blow along `dir` would (scripted scenes: the perf tour's ragdolls, shots):
   *  its health to 0 and `combat:defeated` on the bus, so the bodies module ragdolls it. */
  defeat(id: number, dir?: [number, number, number], severity?: number): void;
  /** The hero's card while it stands (null once defeated: the hero doesn't ragdoll). */
  heroCard: string;
}

const len = (x: number, z: number) => Math.hypot(x, z);
const BUFFERED: Record<string, IntentKind> = { attack: "attack", skill: "skill", bigMove: "big", dodge: "dodge" };

/** Build the fight for a scene. */
export function createFight(host: FightHost): Fight {
  const { collision, actions, nav, content } = host;
  const dt = collision.dt;
  const stepHz = Math.round(1 / dt);
  const maxR = Math.max(...Object.values(content.kinds).map((k) => k.body.radius), 0.5) + 0.1;
  const crowd = new NavCrowd(nav, { maxAgents: 48, maxAgentRadius: maxR, replanEvery: 0.5 });
  crowd.faults.noReseat = FIGHT_FAULTS.crowdNoReseat;
  const enc = host.encounter ? (content.encounters[host.encounter] ?? null) : null;
  if (host.encounter && !enc) throw new Error(`fight: no encounter "${host.encounter}"`);
  const hunt = enc?.hunt ?? true;
  const tokens = new AttackTokens({ cap: content.difficulty[host.difficulty]?.tokenCap ?? content.difficulty.normal?.tokenCap ?? 2, maxHold: enc?.tokens.maxHold ?? 4.5, regrant: enc?.tokens.regrant ?? 1.2 });
  tokens.faults.capRemoved = FIGHT_FAULTS.tokenCapRemoved;
  BRAIN_FAULTS.ignoreTokens = FIGHT_FAULTS.ignoreTokens;
  const wander = host.rng.stream("arena:wander");
  const crits = host.rng.stream("combat");
  const cc = content.combat;
  const heroRow = cc.fighters.get("hero");
  if (!heroRow) throw new Error("fight: no hero fighter row");
  const heroCard = heroRow.card.id;
  const softWeights = targetingValues(content.spike).soft as TargetWeights;

  let world: World | null = null;
  const combatState: CombatState = emptyCombatState();
  const one = <T>(type: string): T => {
    const e = world!.query(type)[0];
    if (e === undefined) throw new Error(`fight: no ${type}`);
    return world!.getComponent<T>(e, type)!;
  };
  const state = (): FightState => one<FightState>(FIGHT_C);
  const enemy = (id: number): EnemyState | undefined => state().enemies.find((e) => e.id === id);
  const archOf = (e: EnemyState): Archetype => content.kinds[e.kind]!;
  const phaseOf = (e: EnemyState) => (e.phase > 1 ? archOf(e).phases[e.phase - 2] : undefined);
  const diff = () => content.difficulty[state().difficulty] ?? content.difficulty.normal!;
  const heroPos = (): Vec3 => {
    const h = host.hero();
    return [h.feet.x, h.feet.y, h.feet.z];
  };
  const heroAlive = (): boolean => combatState.fighters[HERO_ID]?.alive ?? true;

  let emit: <K extends keyof GameEvents>(event: K, payload: GameEvents[K]) => void = () => {};
  const combat = createCombat({
    stepHz,
    rules: cc.rules,
    moves: cc.moves,
    weapons: cc.weapons,
    stats: cc.stats,
    effects: cc.effects,
    ...(host.moveFor ? { moveFor: (e: number, m: string) => host.moveFor!(e, m) } : {}),
    world: collision,
    pose: (id) => {
      if (id === HERO_ID) {
        const h = host.hero();
        return { feet: [h.feet.x, h.feet.y, h.feet.z], yaw: h.yaw };
      }
      const e = enemy(id);
      if (!e) {
        const other = host.otherPose?.(id);
        if (other) return other;
        throw new Error(`fight: no fighter ${id}`);
      }
      return { feet: e.pos, yaw: e.facing };
    },
    state: combatState,
    emit: (k, p) => emit(k, p as never),
    // hit-stop has one owner: the impact director (src/feedback/), by the hit's kind; combat keeps its slow-mo
    clock: { slowMo: (scale, s, ease) => host.slowMo(scale, s, ease) },
    random: () => crits.next(),
    // the soft target when a move starts without one set (the targeting chooser, gate B3, with the soft weights; the
    // hero's lock or soft pick is set by `target` first, the enemies set the hero)
    chooseTarget: (from, candidates) => chooseTarget(from, candidates, softWeights),
    // difficulty (decision 0051): the damage the hero takes and deals, as the difficulty table says
    damageScale: (attacker, target) => (target === HERO_ID ? diff().damageTaken : attacker === HERO_ID ? diff().damageDealt : 1),
  });
  const view = (id: number): FighterView | null => (combat.has(id) ? combat.state(id) : null);
  const port: Brain["combat"] = { request: (e, i) => combat.request(e, i as Intent), state: view, setTarget: (e, t) => combat.setTarget(e, t) };

  const events: FightEvent[] = [];
  let eventsTotal = 0;
  const logEvent = (ev: FightEvent): void => {
    events.push(ev);
    eventsTotal++;
    if (events.length > 600) events.splice(0, events.length - 500);
  };
  const log: AttackLog = { perStep: [], caps: [], logging: false };
  const tally: FightTally = newTally();
  let director: EncounterDirector | null = null;

  // ── bodies, sensors and enemies ──
  const sensorOf = (k: Archetype) => ({ radius: k.body.radius, halfHeight: Math.max(0.05, k.body.height / 2 - k.body.radius) });
  const placeSensor = (id: string, feet: Vec3, s: { radius: number; halfHeight: number }): void => {
    if (collision.has(id)) collision.setPose(id, { x: feet[0], y: feet[1] + s.halfHeight + s.radius, z: feet[2] });
  };
  const heroHurt = { radius: heroRow.hurt.radius, halfHeight: heroRow.hurt.halfHeight };
  const addHeroSensor = (): void => {
    if (collision.has(HERO_HURT_BODY)) return;
    const f = heroPos();
    collision.add({ id: HERO_HURT_BODY, kind: "kinematic", position: { x: f[0], y: f[1] + heroHurt.halfHeight + heroHurt.radius, z: f[2] }, colliders: [{ shape: { type: "capsule", halfHeight: heroHurt.halfHeight, radius: heroHurt.radius }, layer: "hurtHero", sensor: true }] });
  };

  const removeEnemy = (id: number): void => {
    const s = state();
    s.enemies = s.enemies.filter((e) => e.id !== id);
    if (crowd.agent(id)) crowd.remove(id);
    if (combat.has(id)) combat.removeFighter(id);
    if (collision.has(enemyBody(id))) collision.remove(enemyBody(id));
    tokens.forget(id, "death");
    s.knocks = s.knocks.filter((k) => k.entity !== id);
  };

  const bodies: Bodies = createBodies({
    collision,
    tables: bodiesTables(content.spike),
    layer: "ragdoll",
    ...(host.chunkOf ? { chunkOf: host.chunkOf } : {}),
    lookup: {
      card: (id) => {
        if (id === HERO_ID) return heroAlive() ? heroCard : null; // the hero doesn't ragdoll (it stands again)
        const e = enemy(id);
        if (!e) return null;
        const ph = phaseOf(e);
        return ph?.card ?? archOf(e).card;
      },
      root: (id) => {
        if (id === HERO_ID) {
          const h = host.hero();
          return { position: [h.feet.x, h.feet.y, h.feet.z], yaw: h.yaw, velocity: [h.vel.x, h.vel.y, h.vel.z] };
        }
        const e = enemy(id);
        return e ? { position: e.pos, yaw: e.facing, velocity: e.vel } : null;
      },
      knockback: (id, kb) => {
        const steps = Math.max(1, Math.round(kb.seconds / dt));
        const speed = kb.distance / kb.seconds;
        const s = state();
        s.knocks = s.knocks.filter((k) => k.entity !== id);
        s.knocks.push({ entity: id, vel: [kb.dir[0] * speed, kb.dir[2] * speed], left: steps });
      },
      defeated: (id) => {
        // the ragdoll stands in: the enemy's hurt sensor and crowd agent go now, the rest when it's gone
        if (id === HERO_ID) return;
        if (collision.has(enemyBody(id))) collision.remove(enemyBody(id));
        if (crowd.agent(id)) crowd.remove(id);
      },
      gone: (id) => {
        if (id !== HERO_ID && enemy(id)) removeEnemy(id);
      },
    },
  });
  const hasRagdoll = (id: number): boolean => bodies.state().ragdolls.some((r) => r.entity === id);

  const spawn = (kind: string, x: number, z: number, o: { alert?: boolean; facing?: number } = {}): number => {
    const k = content.kinds[kind];
    if (!k) throw new Error(`fight: no enemy kind "${kind}"`);
    const s = state();
    const id = s.nextId++;
    const at = nav.nearest([x, host.terrainAt(x, z), z])?.point ?? [x, host.terrainAt(x, z), z];
    const percept = createPerception();
    if (o.alert) {
      percept.awareness = 1;
      percept.level = "alert";
      percept.unseen = 0;
      percept.lastKnown = heroPos();
      percept.target = s.heroId;
    }
    const hp = heroPos();
    const facing = o.facing ?? Math.atan2(hp[0] - at[0], hp[2] - at[2]);
    const e: EnemyState = { id, kind, phase: 1, alive: true, deadFor: 0, home: [...at] as Vec3, pos: [...at] as Vec3, vel: [0, 0, 0], facing, percept, tree: createTreeState(content.trees[k.tree]!), lastThink: s.steps, hitBy: null, hitFrom: null, wanderAt: null, wanderWait: 0, summoned: false };
    s.enemies.push(e);
    if (o.alert) logEvent({ step: s.steps, type: "alert", p: { entity: id, level: "alert", cause: "spawn" } });
    s.enemies.sort((a, b) => a.id - b.id);
    crowd.add(id, at, { radius: k.body.radius, height: k.body.height, maxSpeed: k.speed.run, maxAccel: k.speed.accel, separation: k.speed.separation });
    const sz = sensorOf(k);
    collision.add({ id: enemyBody(id), kind: "kinematic", position: { x: at[0], y: at[1] + sz.halfHeight + sz.radius, z: at[2] }, colliders: [{ shape: { type: "capsule", halfHeight: sz.halfHeight, radius: sz.radius }, layer: "hurtFoe", sensor: true }] });
    combat.addFighter(id, { team: "enemy", body: enemyBody(id), hurtLayer: "hurtFoe", weapon: k.weapon, stats: { hpMax: k.hp, hp: k.hp, poiseMax: k.poise, poise: k.poise, attack: k.stats.attack, defence: k.stats.defence } });
    return id;
  };
  const spawnAt = (kind: string, at: string, index: number): number => {
    const p = enc?.spawns.find((s) => s.id === at);
    if (!p) throw new Error(`fight: no spawn point "${at}"`);
    // spread a group on a golden-angle spiral (deterministic)
    const a = index * 2.39996;
    const r = p.spread * Math.sqrt((index + 0.5) / 6);
    return spawn(kind, p.x + Math.sin(a) * r, p.z + Math.cos(a) * r, { alert: hunt, ...(p.facing !== undefined ? { facing: p.facing } : {}) });
  };
  const makeDirector = (): EncounterDirector | null => (enc ? new EncounterDirector(enc, { spawn: (kind, at, i) => spawnAt(kind, at, i) }) : null);

  const los = (from: Vec3, to: Vec3): boolean => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const dz = to[2] - from[2];
    const d = Math.hypot(dx, dy, dz);
    if (d < 1e-6) return true;
    const hit = collision.castRay({ x: from[0], y: from[1], z: from[2] }, { x: dx / d, y: dy / d, z: dz / d }, d, { layers: ["terrain", "solid"] });
    return !hit || hit.distance >= d - 0.05;
  };

  const moveScaleOf = (id: number): number => {
    const f = combatState.fighters[id];
    return f && combat.abilities.attributes.has("moveScale") ? combat.abilities.value(f.stats, "moveScale") : 1;
  };
  const brainFor = (e: EnemyState, s: FightState): Brain => {
    const ph = phaseOf(e);
    return {
      e,
      arch: archOf(e),
      // phase D1: a slow status scales the enemy's speeds through its `moveScale` stat (1 when none)
      speedScale: (ph?.speedScale ?? 1) * moveScaleOf(e.id),
      dt,
      hero: { id: s.heroId, pos: heroPos(), alive: heroAlive() },
      crowd,
      tokens,
      combat: port,
      moveset: ph?.moveset ?? archOf(e).moveset,
      snap: (p) => (nav.nearest(p)?.point as Vec3 | undefined) ?? null,
      random: () => wander.next(),
      summon: (kind, count) => {
        for (let i = 0; i < count; i++) {
          const a = e.facing + Math.PI / 2 + i * Math.PI;
          spawn(kind, e.pos[0] + Math.sin(a) * 2.5, e.pos[2] + Math.cos(a) * 2.5, { alert: true });
        }
      },
    };
  };

  const attackers = (): number[] =>
    state()
      .enemies.filter((e) => e.alive)
      .filter((e) => {
        const v = view(e.id);
        return !!v?.alive && !!v.move && (v.move.phase === "windup" || v.move.phase === "active");
      })
      .map((e) => e.id);

  /** The player's combat presses this step: buffered at their first legal moment, the held block, the weapon switch. */
  const playerIntent = (s: FightState): void => {
    const buf = actions.buffer;
    const pending = buf?.action ?? null;
    const hero = HERO_ID;
    const ask = (i: Intent): boolean => {
      if (i.kind !== "dodge" && i.kind !== "block" && i.kind !== "blockRelease") combat.setTarget(hero, fight.target?.() ?? null);
      return combat.request(hero, i);
    };
    if (pending && BUFFERED[pending]) {
      const kind = BUFFERED[pending]!;
      const stick = stickToWorld(actions.stick("move"), host.cameraYaw());
      const dir: Vec3 | undefined = kind === "dodge" && Math.hypot(stick.x, stick.z) > 0.2 ? [stick.x, 0, stick.z] : undefined;
      if (ask(dir ? { kind, dir } : { kind })) buf!.consume(pending);
    } else if (!buf) {
      for (const [action, kind] of Object.entries(BUFFERED)) if (has(action) && actions.pressed(action) && ask({ kind })) break;
    }
    const v = combat.state(hero);
    const held = has("block") && actions.down("block");
    if (held && v.defending !== "block") {
      if (ask({ kind: "block" })) s.heldBlock = true;
    } else if (!held && s.heldBlock && v.defending === "block") {
      ask({ kind: "blockRelease" });
      s.heldBlock = false;
    }
    if (has("weaponSwitch") && actions.pressed("weaponSwitch")) {
      const owned = host.ownedWeapons?.() ?? cc.weapons.filter((w) => cc.attachmentOf.get(w.id)).map((w) => w.id);
      const i = owned.indexOf(v.weapon ?? "");
      combat.setWeapon(hero, owned[(i + 1) % owned.length] ?? null);
    }
  };
  const known = new Set<string>();
  const has = (action: string): boolean => {
    if (known.has(action)) return true;
    try {
      actions.value(action);
      known.add(action);
      return true;
    } catch {
      return false;
    }
  };

  /** Scripted defeats waiting for the next step (not saved: a scripted scene's). */
  const defeats: { id: number; dir: [number, number, number]; severity: number }[] = [];
  const runDefeats = (): void => {
    for (const d of defeats.splice(0)) {
      const st = combatState.fighters[d.id];
      const e = enemy(d.id);
      if (!st?.alive || !e) continue;
      combat.abilities.setBase(st.stats, "hp", 0);
      st.alive = false;
      st.move = null;
      st.defence = { kind: "none", age: 0, perfectUsed: false, dir: [0, 0], release: false };
      emit("combat:defeated", { entity: d.id, by: HERO_ID, point: [e.pos[0], e.pos[1] + 1, e.pos[2]], dir: d.dir, severity: d.severity });
    }
  };

  const place = (s: FightState): void => {
    if (s.placed) return;
    s.placed = true;
    addHeroSensor();
  };

  const module: GameModule<GameEvents> = {
    id: "fight",
    version: "0.1.0",
    setup(ctx) {
      emit = (k, p) => (ctx.events.emit as (event: string, payload: unknown) => void)(k, p);
      ctx.events.on("combat:hit", (p) => {
        const s = state();
        logEvent({ step: s.steps, type: "hit", p });
        if (p.outcome === "dodged" || p.outcome === "perfectDodge") tally.dodged++;
        else if (p.target === s.heroId) tally.heroDamage += p.damage;
        else tally.enemyDamage += p.damage;
        tally.hits[p.move] = (tally.hits[p.move] ?? 0) + 1;
        if (p.target === s.heroId) {
          if (p.outcome === "hit") director?.hurt(p.damage);
          return;
        }
        const e = enemy(p.target);
        if (!e) return;
        if (p.outcome !== "dodged" && p.outcome !== "perfectDodge") {
          e.hitBy = p.attacker;
          const a = p.attacker === s.heroId ? heroPos() : enemy(p.attacker)?.pos;
          e.hitFrom = a ? ([...a] as Vec3) : null;
          // a holder staggered by the hit (its poise broke, its move cut off) gives its token back
          if (tokens.holds(e.id) && (view(e.id)?.stunned ?? 0) > 0) tokens.release(e.id, "hit");
        }
        // a parried attacker is stunned: its token goes back too
        if (p.outcome === "parried" && tokens.holds(p.attacker)) tokens.release(p.attacker, "hit");
      });
      ctx.events.on("combat:defeated", (p) => {
        const s = state();
        logEvent({ step: s.steps, type: "defeated", p });
        if (p.entity === s.heroId) {
          tally.heroDefeats++;
          s.hero.deadFor = 0;
          return;
        }
        const e = enemy(p.entity);
        if (!e && host.otherPose?.(p.entity)) return; // a breakable: its scene's
        tally.defeats[e?.kind ?? "?"] = (tally.defeats[e?.kind ?? "?"] ?? 0) + 1;
        if (!e) return;
        e.alive = false;
        e.deadFor = 0;
        e.vel = [0, 0, 0];
        tokens.forget(e.id, "death");
        const h = heroPos();
        director?.defeated(e.id, len(e.pos[0] - h[0], e.pos[2] - h[2]) < 6);
      });
      ctx.events.on("combat:telegraph", (p) => logEvent({ step: state().steps, type: "telegraph", p }));
      ctx.events.on("combat:move", (p) => {
        const s = state();
        logEvent({ step: s.steps, type: "move", p });
        // a holder's attack is done (landed or missed) once it leaves the active phase: its token goes back
        if ((p.phase === "recovery" || p.phase === "end") && tokens.holds(p.entity)) tokens.release(p.entity, "done");
        if (p.phase === "windup") {
          tally.moves[p.move] = (tally.moves[p.move] ?? 0) + 1;
          const pos = p.entity === s.heroId ? heroPos() : enemy(p.entity)?.pos;
          const range = p.entity === s.heroId ? 10 : (content.kinds[enemy(p.entity)?.kind ?? ""]?.noise ?? 0);
          if (pos && range > 0) s.noises.push({ pos: [...pos] as Vec3, range, source: p.entity, step: s.steps });
        }
      });

      ctx.registerSystem(
        ({ dt: stepDt }) => {
          const s = state();
          s.steps++;
          place(s);
          combat.begin();
          runDefeats();
          // ---- the hero ----
          const h = host.hero();
          if (!heroAlive()) {
            s.hero.deadFor += stepDt;
            if (s.hero.deadFor >= content.tuning.respawn) {
              combat.revive(HERO_ID);
              s.hero.respawns++;
              s.heldBlock = false;
              const at = host.respawnAt();
              host.heroCtl().warp(h, { x: at.x, y: host.terrainAt(at.x, at.z), z: at.z }, at.yaw);
            }
          } else if (fight.heroIntent) {
            const i = fight.heroIntent;
            fight.heroIntent = null;
            if (i.dir && i.kind !== "dodge") h.yaw = Math.atan2(i.dir[0], i.dir[2]);
            if (i.kind !== "dodge" && i.kind !== "block" && i.kind !== "blockRelease") combat.setTarget(HERO_ID, fight.target?.() ?? null);
            combat.request(HERO_ID, i);
          } else if (!fight.botDriving) playerIntent(s);
          if (heroAlive() && Math.hypot(h.vel.x, h.vel.z) > 4.5 && s.steps % 30 === 0) s.noises.push({ pos: heroPos(), range: 4, source: s.heroId, step: s.steps });
          // ---- a boss's phases: its card and its moves change (its weapon between moves: combat swaps weapons only
          //      then, so a phase entered mid-swing takes its new moves once the swing is done) ----
          for (const e of s.enemies) {
            if (!e.alive) continue;
            const k = archOf(e);
            const st = view(e.id);
            const want = phaseOf(e)?.weapon ?? k.weapon;
            if (st && st.weapon !== want && !st.move) combat.setWeapon(e.id, want);
            const next = k.phases[e.phase - 1];
            if (next && st && st.hp <= st.hpMax * next.atHp) {
              if (!st.move) combat.setWeapon(e.id, next.weapon);
              e.phase++;
              e.summoned = false;
              if (crowd.agent(e.id)) crowd.updateSpec(e.id, { maxSpeed: k.speed.run * next.speedScale, maxAccel: k.speed.accel * next.speedScale });
              logEvent({ step: s.steps, type: "phase", p: { entity: e.id, phase: e.phase } });
            }
          }
          // ---- the director ----
          if (director && s.directorOn) {
            director.step(stepDt);
            // a wave hunts: the director pings where the hero is as a noise every enemy hears (Left 4 Dead's mobs
            // know where the survivors are)
            const ping = Math.max(1, Math.round(content.tuning.huntPing / stepDt));
            if (hunt && director.state.phase === "wave" && s.steps % ping === 0 && heroAlive()) s.noises.push({ pos: heroPos(), range: 1000, source: s.heroId, step: s.steps });
          }
          // ---- the brains ----
          const every = content.tuning.thinkEvery;
          const hp = heroPos();
          const candidates = heroAlive() ? [{ id: s.heroId, pos: hp, height: 1 }] : [];
          s.noises = s.noises.filter((n) => s.steps - n.step <= every);
          for (const e of [...s.enemies]) {
            if (!e.alive) continue;
            if ((s.steps + e.id) % every === 0) {
              const before = e.percept.level;
              const noises = s.noises.filter((n) => n.step > e.lastThink && n.source !== e.id);
              perceive(e.percept, { pos: e.pos, facing: e.facing }, { candidates, noises, hitBy: e.hitBy, ...(e.hitFrom ? { hitFrom: e.hitFrom } : {}) }, archOf(e).senses, los, (s.steps - e.lastThink) * stepDt);
              e.lastThink = s.steps;
              e.hitBy = null;
              e.hitFrom = null;
              if (e.percept.level !== before) logEvent({ step: s.steps, type: "alert", p: { entity: e.id, level: e.percept.level, cause: e.percept.cause } });
            }
            tickTree(content.trees[archOf(e).tree]!, e.tree, brainFor(e, s), stepDt);
          }
          tokens.arbitrate(stepDt);
        },
        { name: "fight:act", order: -10 },
      );

      ctx.registerSystem(
        ({ dt: stepDt }) => {
          const s = state();
          const h = host.hero();
          const hp = heroPos();
          // ---- the crowd: the hero placed for the others to avoid; combat's drive or a knockback over the brains ----
          crowd.place(s.heroId, hp, [h.vel.x, 0, h.vel.z]);
          for (const e of s.enemies) {
            if (!e.alive || !crowd.agent(e.id)) continue;
            const kb = s.knocks.find((k) => k.entity === e.id);
            if (kb) {
              crowd.moveVelocity(e.id, [kb.vel[0], 0, kb.vel[1]]);
              continue;
            }
            const d = combat.drive(e.id);
            if (!d) continue;
            if (d.yaw !== undefined) e.facing = d.yaw;
            if (Math.hypot(d.x, d.z) < 1e-6) crowd.stop(e.id);
            else crowd.moveVelocity(e.id, [d.x, 0, d.z]);
          }
          crowd.step(stepDt);
          for (const e of s.enemies) {
            if (!e.alive) {
              e.deadFor += stepDt;
              continue;
            }
            const a = crowd.agent(e.id);
            if (!a) continue;
            e.pos = [...a.pos] as Vec3;
            e.vel = [...a.vel] as Vec3;
            const sp = len(a.vel[0], a.vel[2]);
            const busy = view(e.id)?.move != null;
            // face where it walks; a busy enemy keeps the move's facing
            if (sp > 0.4 && !busy) {
              const want = Math.atan2(a.vel[0], a.vel[2]);
              const d = Math.atan2(Math.sin(want - e.facing), Math.cos(want - e.facing));
              const step = archOf(e).speed.turn * (phaseOf(e)?.speedScale ?? 1) * stepDt;
              e.facing += Math.max(-step, Math.min(step, d));
            }
            placeSensor(enemyBody(e.id), e.pos, sensorOf(archOf(e)));
          }
          placeSensor(HERO_HURT_BODY, hp, heroHurt);
          for (const k of s.knocks) k.left--;
          s.knocks = s.knocks.filter((k) => k.left > 0);
          // a defeated enemy with no ragdoll (no part list) goes after the corpse time; a ragdoll's goes when it's gone
          for (const e of [...s.enemies]) if (!e.alive && e.deadFor >= content.tuning.corpse && !hasRagdoll(e.id)) removeEnemy(e.id);
        },
        { name: "fight:move", order: 50 },
      );

      ctx.registerSystem(
        () => {
          combat.resolve();
          const s = state();
          // plain-data copies for the save and the hash
          s.tokens = tokens.snapshot();
          s.director = director ? director.snapshot() : null;
          if (log.logging) {
            log.perStep.push(attackers().length);
            log.caps.push(tokens.cap);
          }
        },
        { name: "fight:resolve", order: 150 },
      );
      ctx.registerSaveSlice({
        serialize: () => ({ combat: structuredClone(combatState), state: structuredClone(state()), crowd: crowd.snapshot() }),
        deserialize: (data) => {
          const d = data as { combat?: CombatState; state?: FightState; crowd?: CrowdSnapshot; fresh?: boolean };
          const s = state();
          // a save from before the fight joined its scene (src/save/schema.ts's migration): a fight not yet begun
          const fresh = d.fresh === true || !d.state;
          const next: FightState = fresh ? initial(s.difficulty, s.directorOn) : structuredClone(d.state!);
          for (const k of Object.keys(s)) delete (s as unknown as Record<string, unknown>)[k];
          Object.assign(s, next);
          combatState.fighters = fresh ? {} : structuredClone(d.combat!.fighters);
          crowd.restore(fresh ? { format: "rpg.nav.crowd@1", agents: [], steps: 0 } : d.crowd!);
          tokens.restore(s.tokens);
          director = makeDirector();
          if (director && s.director) director.restore(s.director);
          if (fresh) addHero();
        },
      });
    },
  };

  const initial = (difficulty: string, directorOn: boolean): FightState => ({
    nextId: 1,
    heroId: HERO_ID,
    difficulty,
    hero: { deadFor: 0, respawns: 0 },
    enemies: [],
    tokens: { cap: content.difficulty[difficulty]?.tokenCap ?? 2, held: [], rest: [] },
    director: null,
    directorOn,
    noises: [],
    steps: 0,
    placed: false,
    knocks: [],
    heldBlock: false,
  });

  const addHero = (): void => {
    if (combat.has(HERO_ID)) combat.removeFighter(HERO_ID);
    combat.addFighter(HERO_ID, { team: heroRow.team, body: HERO_HURT_BODY, hurtLayer: "hurtHero", weapon: heroRow.weapon?.id ?? null, stats: heroRow.stats as Record<string, number> });
    const hp = heroPos();
    if (!crowd.agent(HERO_ID)) crowd.add(HERO_ID, hp, { radius: 0.2, height: 1.7, maxSpeed: 8, maxAccel: 40 }, { external: true });
  };

  const fight: Fight = {
    modules: [module, bodies.module],
    build(w) {
      world = w;
      const difficulty = content.difficulty[host.difficulty] ? host.difficulty : "normal";
      const s = initial(difficulty, host.director);
      w.addComponent<FightState>(w.createEntity(), FIGHT_C, s);
      w.addComponent<CombatState>(w.createEntity(), FIGHT_COMBAT_C, combatState);
      tokens.cap = diff().tokenCap;
      addHero();
      director = makeDirector();
      s.director = director?.snapshot() ?? null;
      s.tokens = tokens.snapshot();
    },
    hashed: FIGHT_HASHED,
    combat,
    bodies,
    collision,
    crowd,
    tokens,
    get director() {
      return director;
    },
    content,
    dt,
    state,
    combatState: () => combatState,
    view,
    heroDrive: () => {
      if (!world) return null;
      if (!heroAlive()) return { x: 0, z: 0 };
      const kb = state().knocks.find((k) => k.entity === HERO_ID);
      if (kb) return { x: kb.vel[0], z: kb.vel[1] };
      return combat.drive(HERO_ID);
    },
    heroIntent: null,
    botDriving: false,
    target: null,
    events,
    eventsTotal: () => eventsTotal,
    log,
    tally,
    attackers,
    spawn,
    reset(o = {}) {
      const s = state();
      for (const e of [...s.enemies]) removeEnemy(e.id);
      if (o.difficulty && content.difficulty[o.difficulty]) s.difficulty = o.difficulty;
      tokens.restore({ cap: diff().tokenCap, held: [], rest: [] });
      s.noises = [];
      s.knocks = [];
      combat.revive(HERO_ID);
      s.hero = { deadFor: 0, respawns: 0 };
      const at = host.respawnAt();
      host.heroCtl().warp(host.hero(), { x: at.x, y: host.terrainAt(at.x, at.z), z: at.z }, at.yaw);
      crowd.place(s.heroId, heroPos(), [0, 0, 0]);
      if (o.director !== undefined) s.directorOn = o.director;
      director = makeDirector();
      s.director = director?.snapshot() ?? null;
      s.tokens = tokens.snapshot();
      events.length = 0;
      Object.assign(tally, newTally());
    },
    setDirector(on) {
      state().directorOn = on;
    },
    directorOn: () => state().directorOn,
    engaged: () => heroAlive() && state().enemies.some((e) => e.alive && e.percept.level === "alert"),
    defeat: (id, dir = [0, 0, 1], severity = 0.8) => void defeats.push({ id, dir, severity }),
    heroCard,
  };
  return fight;
}
